import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Accordion,
  ListGroup,
} from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { api } from '../components/api';
import { Calendar, CreditCard, BedDouble, CheckCircle } from 'lucide-react';
import TermsModal from '../components/Termsmodel';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const normalizeReservation = (res) => ({
  reservationId: res.uuid,
  checkIn: res.checkInDate,
  checkOut: res.checkOutDate,
  nights: res.totalNights,
  specialrequest : res.specialRequest ? res.specialRequest : '',
  adults: res.adults,
  childrens: res.childrens ? res.childrens : 0,
  contact: {
    email: res.customerDetail?.email || '',
    firstName: res.customerDetail?.firstName || '',
    lastName: res.customerDetail?.lastName || '',
    phone: res.customerDetail?.phone || '',
  },
  totalAmount: res.reservationTotalAmountWithoutTax,
  paymentType: res.paymentTypeStr,
  status: res.status,
  rooms: res.roomBookingDetails?.map((room) => ({
    type: room.roomTypeStr,
    units: room.unitsReserved,
    baseRate: room.baserate,
  })),
});

function GlobalAlert({ message, onClose }) {
  if (!message) return null;

  return (
    <Alert
      variant="dark"
      dismissible
      onClose={onClose}
      className="position-fixed top-50 start-50 translate-middle text-center shadow rounded"
      style={{
        zIndex: 1050,
        minWidth: '300px',
        maxWidth: '90vw',
        backgroundColor: '#212529',
        color: '#fff',
      }}
    >
      {message}
      <style>{`
        .btn-close {
          filter: invert(1) grayscale(100%) brightness(200%);
          opacity: 1;
        }
      `}</style>
    </Alert>
  );
}

function PaymentForm({ reservation, setPageError }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [email, setEmail] = useState(reservation.contact?.email || '');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setPageError('');
    setLoading(true);

    if (!stripe || !elements) {
      setPageError('Stripe not ready');
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (error) {
      setPageError(error.message);
      setLoading(false);
      return;
    }

    try {
          const response = await api.post('/reservation/confirm', {
        reservationId: reservation.reservationId,
        paymentMethodId: paymentMethod.id,
        email,
      });

    const { customerSecrete, requiredAction } = response.data;


      if (requiredAction && customerSecrete) {
          const result = await stripe.confirmCardSetup(customerSecrete, {
            payment_method: paymentMethod.id,
          });
    
          if (result.error) {
            setPageError(result.error.message || '3D Secure failed');
            setLoading(false);
            return;
          }
    
          // Call backend again to finalize
          await api.post('/reservation/confirm-complete', {
              reservationId: reservation.reservationId,
              paymentMethodId: paymentMethod.id,
              setupIntentId: result.setupIntent.id,
              email
          });
        }

      navigate('/confirmation', { state: { reservation } });
    } catch (err) {
      if (err?.response?.data?.status === 510) {
        setPageError(
          <>
            We’re sorry, but there aren’t enough rooms available to fulfill your original reservation request.
            Please start a new search to find available options.{' '}
            <a
              href="/"
              onClick={(ev) => {
                ev.preventDefault();
                navigate('/');
              }}
              style={{ textDecoration: 'underline', color: 'white' }}
            >
              New Reservation
            </a>.
          </>
        );
      } else {
        setPageError(
          err?.response?.data?.message || 'Error submitting reservation. If error persisted kindly call motel at +1 705-635-2400'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleConfirm}>
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold fs-8 text-dark">
          <small className="text-muted">(Please update email here if it is incorrect.)</small>
        </Form.Label>
        <Form.Control
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <div className="border p-2 rounded">
          <CardNumberElement />
        </div>
      </Form.Group>

      <Form.Group className="mb-3">
        <div className="border p-2 rounded">
          <CardExpiryElement />
        </div>
      </Form.Group>

      <Form.Group className="mb-3">
        <div className="border p-2 rounded">
          <CardCvcElement />
        </div>
      </Form.Group>

      <Button type="submit" variant="dark" disabled={!stripe || loading} className="w-100">
        {loading ? <Spinner animation="border" size="sm" /> : 'Confirm Your Reservation'}
      </Button>
    </Form>
  );
}

function ReservationSummary({ reservation }) {
  const [defaultKey, setDefaultKey] = useState('');

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    setDefaultKey(isMobile ? null : '0');
  }, []);


 
  const taxRate = 0.13;
  const matRate = 0.04;
  const taxAmount = Math.round((reservation.totalAmount ) * taxRate * 100) / 100;
  const matAmount =    Math.round((reservation.totalAmount ) * matRate * 100) / 100;
    const matTaxHST = Math.round((  matAmount) * taxRate * 100) / 100;
  const totalMATWithHST = Math.round((matAmount + matTaxHST) *100) / 100;
  const totalTaxes = Math.round((totalMATWithHST + taxAmount) * 100) / 100;

  const totalWithTax = Math.round((reservation.totalAmount + totalTaxes) * 100) / 100;
return (
    <Accordion defaultActiveKey={defaultKey} alwaysOpen={!defaultKey}>
      <Card className="shadow-sm border-0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <strong>Reservation Summary</strong>
            <span className="ms-2 small text-muted">
              {reservation.status === 'DRAFT'
                ? 'Not Confirmed'
                : reservation.status === 'NEW'
                ? 'Confirmed'
                : reservation.status}
            </span>
          </Accordion.Header>
          <Accordion.Body>
            <ListGroup variant="flush" className="mb-3">
              <ListGroup.Item>
                <Calendar size={16} className="me-2" />
                <strong>Check-in:</strong> {reservation.checkIn}
              </ListGroup.Item>
              <ListGroup.Item>
                <Calendar size={16} className="me-2" />
                <strong>Check-out:</strong> {reservation.checkOut}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Total Nights:</strong> {reservation.nights}
              </ListGroup.Item>
                 <ListGroup.Item>
                <strong>Adults:</strong> {reservation.adults}
              </ListGroup.Item>
                 <ListGroup.Item>
                <strong>Children:</strong> {reservation.childrens}
              </ListGroup.Item>
            </ListGroup>

            <ListGroup variant="flush" className="mb-3">
              <ListGroup.Item>
                <strong>Amount (Before Tax):</strong> ${reservation.totalAmount.toFixed(2)}
              </ListGroup.Item>
               <ListGroup.Item>
                <strong>Municipal Tax (MAT):</strong> ${totalMATWithHST.toFixed(2)}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Goverment Tax (HST):</strong> ${taxAmount.toFixed(2)}
              </ListGroup.Item>
              <ListGroup.Item className="fw-bold">
                <CheckCircle size={16} className="me-2" />
                <strong>Total (After Tax):</strong> ${totalWithTax.toFixed(2)}
              </ListGroup.Item>
            </ListGroup>

            <ListGroup variant="flush" className="mb-3">
              <ListGroup.Item>
                <CreditCard size={16} className="me-2" />
                <strong>Payment Method:</strong> {reservation.paymentType}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Status:</strong>{' '}
                <span
                  style={{
                    color:
                      reservation.status === 'DRAFT'
                        ? 'red'
                        : reservation.status === 'NEW'
                        ? 'green'
                        : 'black',
                  }}
                >
                  {reservation.status === 'DRAFT'
                    ? 'Not Confirmed'
                    : reservation.status === 'NEW'
                    ? 'Confirmed'
                    : reservation.status}
                </span>
              </ListGroup.Item>

                  <ListGroup.Item>
                  <strong>Special request:</strong> {reservation.specialrequest}
              </ListGroup.Item>

            </ListGroup>

            <div>
              <strong>Rooms:</strong>
              <ListGroup variant="flush" className="ms-2 mt-2">
                {reservation.rooms?.map((room, i) => (
                  <ListGroup.Item key={i}>
                    <div>
                      <BedDouble size={16} className="me-2" />
                      <strong>{room.type}</strong>
                    </div>
                    <div>{room.units} room(s)</div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

               
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Card>
    </Accordion>
  );
}

export default function ConfirmReservation() {
  const { reservationId } = useParams();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const navigate = useNavigate();
    const [showTermsModal, setShowTermsModal] = useState(false);

 useEffect(() => {
  const fetchReservation = async () => {
    try {
      const res = await fetch(`/api/reservation/public/${reservationId}`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      const normalized = normalizeReservation(data);
      setReservation(normalized); // Only set if successful

      // Now safe to call checkIfBookable
      await checkIfBookable();
    } catch (err) {
      console.error('Error fetching reservation:', err);

      if (err?.response?.data?.status === 510) {
        setPageError(
          'We’re sorry, but there aren’t enough rooms available to fulfill your original reservation request. Please start a new search.'
        );
      } else {
        setPageError(
          err?.response?.data?.message || 'Reservation not found.'
        );
      }

      setReservation(null); // Ensure state is clean if error occurs
    } finally {
      setLoading(false);
    }
  };

  const checkIfBookable = async () => {
    try {
      const res = await fetch(`/api/reservation/bookable/${reservationId}`);
      const data = await res.json();
      if (data?.status === 510) {
             setPageError(
          <>
            We’re sorry, but there aren’t enough rooms available to fulfill your original reservation request.
            Please start a new search to find available options.{' '}
            <a
              href="/"
              onClick={(ev) => {
                ev.preventDefault();
                navigate('/');
              }}
              style={{ textDecoration: 'underline', color: 'white' }}
            >
              New Reservation
            </a>.
          </>
        );
      }
    } catch (err) {
      console.error('Error checking bookable:', err);
      setPageError('Error checking room availability.');
    }
  };

  fetchReservation();
}, [reservationId]);

  if (loading) return <div className="p-3"><Spinner animation="border" /></div>;
  if (!reservation) return <div className="p-3 text-danger">No reservation found.</div>;

  function capitalizeName(name) {
    return name
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  const fullName1 = `${reservation?.contact.firstName || ''} ${reservation?.contact.lastName || ''}`.trim();
  const fullName = capitalizeName(fullName1);

  return (
    <>
    <div className="container py-4">
      <GlobalAlert message={pageError} onClose={() => setPageError('')} />

      <h4 className="mb-4 text-center text-md-start">
        Your Reservation, <strong>{fullName || 'Guest'}</strong> — from <strong>{reservation?.checkIn}</strong>
      </h4>

      <div className="row flex-md-row-reverse g-4">
        <div className="col-md-5">
          <div style={{ position: 'sticky', top: '100px' }}>
            <ReservationSummary reservation={reservation} />
          </div>
        </div>

        <div className="col-md-7">
          <Card className="p-4 shadow-sm border-0">
            <p className="mb-4 text-muted small">
              To secure your reservation, a valid credit card is required.
              Rest assured, your card will not be charged until your arrival.
              <br /><br />
              If you prefer not to provide a credit card online, please feel free to call us directly at{' '}
              <a href="tel:+17056352400">+1 705-635-2400</a>.
            </p>
            <Elements stripe={stripePromise}>
              <PaymentForm reservation={reservation} setPageError={setPageError} />
            </Elements>
            <div className="mt-4">
              <h6>Cancellation Policy</h6>
              <p className="small text-muted mb-3">

Cancellations made 30 or more days before check-in will not incur any charges.
<br /><br /> Cancellations made 30 to 14 days before check-in will incur a 50% charge of the total reservation amount plus taxes.<br /><br /> Cancellations made within 14 days of check-in, as well as no-shows, will be charged in full plus taxes.

              </p>
              <p className="text-muted small mt-3">
  By continuing, you agree to our{' '}
 <a
  href="#"
  className="text-decoration-none ms-1"
  onClick={(e) => {
    e.preventDefault();
    setShowTermsModal(true);
  }}
>
  Terms and Conditions
</a>

</p>



              <h6>Security & Payments</h6>
              <p className="small text-muted mb-3">
                This site is protected by reCAPTCHA. Google's{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>{' '}and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}apply.
              </p>
              <div className="d-flex align-items-center gap-3">
                <img src="/assets/pci-dss-certified.png" alt="PCI Secure" height="40" />
                <img src="/assets/stripe-payment-secure.jpg" alt="Stripe Secure" height="40" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
            <TermsModal show={showTermsModal} onClose={() => setShowTermsModal(false)} />


</>
  );

  
}
